import { FetchResult } from '@apollo/client';
import { createError } from '@common/neo.tax';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { useUpdatePayrollExpense } from '@neo.tax/rd/hooks';
import { isPayrollQreWithPayrollExpense, PayrollQre, WithPayrollExpense } from '@neo.tax/rd/hooks/usePayrollQres';
import { ToastConfigState, UpdatePayrollExpenseMutation } from '@neo.tax/shared';

import { ContributorGeneralFormType } from '../types';

const makeDefaultFormValues = (payrollQre: WithPayrollExpense<PayrollQre>): ContributorGeneralFormType => ({
  id: payrollQre.payrollExpenseId,
  fullName: payrollQre.name ?? '',
});

interface UseContributorGeneralFormProps {
  payrollQre: PayrollQre;
}

export const usePayrollExpenseGeneralForm = ({
  payrollQre,
}: UseContributorGeneralFormProps): UseFormReturn<ContributorGeneralFormType> => {
  // https://linear.app/neo-tax/issue/NEO-6221/determine-what-to-do-with-officiers-on-credit-calculation-page
  // https://linear.app/neo-tax/issue/NEO-6217/fix-check-type-issue-with-randd-changes
  // The incoming qre is coming from graphql query getPayrollExpense field `payrollQre`
  // This is a shared type between PayrollQre and PayrollExpense which makes payrollExpenseId optional
  // We need to check if the payroll QRE has a payroll expense, and if not, throw an error
  // Note, in PayrollQreLayout, this component is not rendered if the payroll QRE has no payroll expense
  // so in theory this code should never be reached.
  // It's simply here to enforce the type WithPayrollExpense<PayrollQre> further down the line
  if (!isPayrollQreWithPayrollExpense(payrollQre)) {
    throw createError('MissingPayrollExpense', 'Payroll QRE has no payroll expense');
  }

  const [updatePayrollExpense] = useUpdatePayrollExpense();

  const form = useForm<ContributorGeneralFormType>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(payrollQre),
  });
  const { handleSubmit, watch } = form;
  const currentPayrollExpenseId = watch('id');

  const onValid: SubmitHandler<ContributorGeneralFormType> = useCallback(
    (values): Promise<FetchResult<UpdatePayrollExpenseMutation>> | undefined => {
      // Don't unnecessarily update the expense if the name hasn't changed
      if (values.fullName === payrollQre.name) {
        return;
      }

      return updatePayrollExpense(payrollQre.payrollExpenseId, {
        amountCents: payrollQre.amountCents,
        countryAlpha2: payrollQre.country,
        fullName: values.fullName,
        state: payrollQre.state,
        title: payrollQre.title,
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [updatePayrollExpense, payrollQre.payrollExpenseId],
  );

  const onInvalid = (): void => {
    ToastConfigState.pushErrorToast('Error updating expense', {
      description: 'One or more values in the form are invalid. Please correct them to save.',
      duration: 'MEDIUM',
    });
  };
  const onInvalidDebounced = useDebouncedCallback(onInvalid, 500);

  useEffect(() => {
    const subscription = watch(() => handleSubmit(onValid, onInvalidDebounced)());
    return (): void => subscription.unsubscribe();
  }, [handleSubmit, onInvalidDebounced, onValid, watch]);

  useEffect(() => {
    if (currentPayrollExpenseId !== payrollQre.payrollExpenseId) {
      form.reset(makeDefaultFormValues(payrollQre));
    }
    // We only want to reset the form when the payroll expense changes
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [payrollQre.payrollExpenseId, currentPayrollExpenseId, form]);

  return form;
};
